export type GalleryFilter = "all" | "completed" | "favorites" | "trash";
export type GalleryMedia = "all" | "image" | "video" | "audio";

export function matchesGalleryMedia(job: { capability_key: string; result: { assets?: { type: string }[] } }, media: GalleryMedia): boolean {
  if (media === "all") return true;
  const assets = job.result.assets ?? [];
  if (assets.length) return assets.some((asset) => asset.type === media);
  const key = job.capability_key;
  return (key.includes("video") ? "video" : /audio|speech|tts/.test(key) ? "audio" : "image") === media;
}

export const GALLERY_PAGE_SIZE = 24;

/** The last job of the previous page; the next page starts strictly after it. */
export type GalleryCursor = { id: string; created_at: string | null };

/** Keyset cursor for page `index` of an infinite list, from the page before it. */
export function galleryCursor(previous: GalleryCursor[] | null | undefined): GalleryCursor | null {
  return previous?.length ? previous[previous.length - 1] : null;
}

export function galleryPageKey({
  filter,
  projectId,
  userId,
  batchId,
  media = "all",
  page,
  pageSize = GALLERY_PAGE_SIZE,
  cursor = null,
}: {
  filter: GalleryFilter;
  projectId?: string | null;
  userId?: string | null;
  batchId?: string | null;
  media?: GalleryMedia;
  page: number;
  pageSize?: number;
  /** Pages after the first follow the previous page's last job, so new
   *  generations arriving at the top never shift, skip or repeat items. */
  cursor?: GalleryCursor | null;
}): string {
  const params = new URLSearchParams();
  params.set("limit", String(pageSize));
  if (cursor?.created_at) {
    params.set("before_created_at", cursor.created_at);
    params.set("before_id", cursor.id);
  } else if (page > 0) {
    params.set("offset", String(page * pageSize));
  }
  if (projectId) params.set("project_id", projectId);
  if (userId) params.set("user_id", userId);
  if (batchId) params.set("batch_id", batchId);
  if (media !== "all") params.set("media_type", media);
  if (filter === "completed") params.set("state", "completed");
  if (filter === "favorites") params.set("favorite", "true");
  if (filter === "trash") params.set("trash", "true");
  return `/api/jobs?${params.toString()}`;
}
